import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface MagneticButtonProps {
  children: ReactNode;
  className?: string;
  /** How strongly the button pulls toward cursor (0-1). Default 0.25. */
  strength?: number;
}

/**
 * MagneticButton — wraps children in a span that pulls toward the cursor.
 * Vanilla RAF implementation (no Framer Motion). Auto-disabled on touch
 * (pointer:coarse) and prefers-reduced-motion via early return.
 *
 * Subtle effect (Emil philosophy) : pull max 30% of cursor distance from
 * center, easing exponentially back on pointerleave.
 */
export function MagneticButton({ children, className, strength = 0.25 }: MagneticButtonProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const rafRef = useRef(0);
  const currentX = useRef(0);
  const currentY = useRef(0);
  const targetX = useRef(0);
  const targetY = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const node = ref.current;
    if (!node) return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const animate = () => {
      currentX.current += (targetX.current - currentX.current) * 0.2;
      currentY.current += (targetY.current - currentY.current) * 0.2;
      node.style.transform = `translate3d(${currentX.current}px, ${currentY.current}px, 0)`;
      if (
        Math.abs(targetX.current - currentX.current) > 0.1 ||
        Math.abs(targetY.current - currentY.current) > 0.1
      ) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        rafRef.current = 0;
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      const rect = node.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      targetX.current = (e.clientX - cx) * strength;
      targetY.current = (e.clientY - cy) * strength;
      if (!rafRef.current) rafRef.current = requestAnimationFrame(animate);
    };

    const onPointerLeave = () => {
      targetX.current = 0;
      targetY.current = 0;
      if (!rafRef.current) rafRef.current = requestAnimationFrame(animate);
    };

    node.addEventListener('pointermove', onPointerMove, { passive: true });
    node.addEventListener('pointerleave', onPointerLeave);

    return () => {
      node.removeEventListener('pointermove', onPointerMove);
      node.removeEventListener('pointerleave', onPointerLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [strength]);

  return (
    <span ref={ref} className={cn('inline-block will-change-transform', className)}>
      {children}
    </span>
  );
}
